// Request validation. The same schemas validate API input on the server and
// forms on the client.

import { z } from 'zod';
import { AMBUSH_STYLES, ATTRS, BACKGROUNDS, GEAR_SLOTS, OUTFIT_SET_IDS, RETREATS, STANCES } from './types';
import { AUTO_DISMANTLE_RARITIES, BULK_DISMANTLE_MAX, normalizeAutoDismantle } from './items/inventory';
import { BODY_TYPES } from './characters';
import { NAME_ISSUE_TEXT, nameIssue } from './names';
import { CREATION_FIELDS } from './onboarding';
import { CRAFT_QUEUE_LIMIT } from './content/recipes';
import { RELIC_FORGE, RELIC_SLOTS } from './items/relicForge';
import { OUTFIT_RARITIES } from './outfits';

export const zStance = z.enum(STANCES);
export const zRetreat = z.enum(RETREATS);
export const zAmbush = z.enum(AMBUSH_STYLES);
export const zBackground = z.enum(BACKGROUNDS);
export const zZone = z.enum(['forest', 'gas_station', 'downtown', 'hospital', 'factory', 'military_base', 'mountains', 'wasteland', 'secret_bunker', 'underground_lab', 'survivor_vault']);
export const zStackKind = z.enum([
  'food', 'wood', 'cloth', 'metal', 'fuel', 'electronics', 'chemicals', 'medicine', 'ammo', 'scrap',
  'bandage', 'medkit', 'painkillers', 'ration', 'dried_jerky', 'hot_stew', 'cold_beans', 'canned_feast', 'stim', 'repair_kit', 'precision_repair_kit', 'mend_shot', 'hollow_point', 'molotov',
  'station9_chili', 'black_coffee', 'halcyon_bar', 'canned_peaches', 'fever_broth', 'night_stew', 'horde_ration',
  'old_map', 'map_fragment', 'blueprint', 'lab_clue', 'relic_shard', 'mend_coupon', 'red_berries', 'mercy_postcard', 'mend_blood', 'plan_b_binder',
]);
export const zTradeKind = z.enum(['food', 'wood', 'cloth', 'metal', 'fuel', 'electronics', 'chemicals', 'medicine', 'ammo', 'scrap', 'bandage', 'medkit']);

/** 3–16 characters, letters, numbers, space, - and _, no swear words (8.1). Not unique. */
export const survivorNameSchema = z
  .string()
  .trim()
  .superRefine((s, ctx) => {
    const issue = nameIssue(s);
    if (issue) ctx.addIssue({ code: 'custom', message: NAME_ISSUE_TEXT[issue], params: { issue } });
  });

export const zBodyType = z.enum(BODY_TYPES);

export const appearanceSchema = z.object({
  build: z.enum(['lean', 'sturdy']).default('lean'),
  skin: z.number().int().min(0).max(5).default(2),
  hair: z.number().int().min(0).max(5).default(0),
  hairColor: z.number().int().min(0).max(5).default(1),
  beard: z.number().int().min(0).max(3).default(0),
  outfit: z.enum(['moss', 'rust', 'steel', 'sand']).default('moss'),
  headwear: z.enum(['none', 'cap', 'hat', 'hood']).default('none'),
  pack: z.enum(['satchel', 'hiking', 'duffel']).default('satchel'),
});

export const createSurvivorSchema = z.object({
  name: survivorNameSchema,
  background: zBackground,
  /** Male or female picture (8.1); cannot be changed later. */
  bodyType: zBodyType,
  /** Kept for old clients; the look is now the body type only. */
  appearance: appearanceSchema.optional(),
  ref: z.string().max(32).optional().nullable(),
  /** Creation screen analytics (docs/onboarding.md, section 5): attempt, time to create, fields changed. */
  creation: z
    .object({
      session: z.string().min(8).max(40),
      ms: z.number().int().min(0).max(24 * 3_600_000),
      changed: z.array(z.enum(CREATION_FIELDS)).max(CREATION_FIELDS.length),
    })
    .optional(),
  /** Date.getTimezoneOffset() of the device: contracts reset at this local midnight (E3). */
  tz: z.number().int().min(-840).max(840).optional(),
});

/** Survivors from before section 8 pick their look once. */
export const setBodyTypeSchema = z.object({ bodyType: zBodyType });

/** Creation screen analytics, sent before a survivor (or even a user) exists (docs/onboarding.md, section 5). */
export const creationEventSchema = z.discriminatedUnion('event', [
  z.object({ event: z.literal('view'), session: z.string().min(8).max(40) }),
  z.object({ event: z.literal('field'), session: z.string().min(8).max(40), field: z.enum(CREATION_FIELDS) }),
  z.object({ event: z.literal('name_error'), session: z.string().min(8).max(40), reason: z.enum(['short', 'long', 'chars', 'letter', 'profanity']) }),
]);
export type CreationEventBody = z.infer<typeof creationEventSchema>;

/** The "Save your survivor" card on Camp (docs/onboarding.md, section 6). */
export const linkPromptSchema = z.object({ action: z.enum(['shown', 'accepted', 'dismissed']) });

/** Krug 5 (K6.2): the auto-dismantle offer on the Camp; `dismantleNow` also dismantles the Common gear there is. */
export const autoDismantlePromptSchema = z.object({ action: z.enum(['shown', 'accepted', 'dismissed']), dismantleNow: z.boolean().optional() });

export const allocateSchema = z.object({
  add: z.object(Object.fromEntries(ATTRS.map((k) => [k, z.number().int().min(0).max(60).default(0)])) as Record<(typeof ATTRS)[number], z.ZodDefault<z.ZodNumber>>),
});

/** Krug 5 (K10): the pending nodes, sent together with Apply. */
export const applySkillsSchema = z.object({ nodes: z.array(z.string().min(1).max(40)).min(1).max(19) });
/** K10.9: Skills screen analytics (opened; a keystone path looked at). */
export const skillEventSchema = z.object({ event: z.enum(['open', 'preview']), node: z.string().min(1).max(40).optional(), device: z.enum(['desktop', 'mobile']) });

export const sendExpeditionSchema = z.object({
  zone: zZone,
  duration: z.number().int().min(1).max(600),
  // Krug 5 (K16.2): a closed system is not on the screen, so its field may be missing: the defaults.
  stance: zStance.default('cautious'),
  retreat: zRetreat.default('50'),
  ambushStyle: zAmbush.default('steal'),
  offer: z.array(zTradeKind).max(6).default([]),
  want: z.array(zTradeKind).max(6).default([]),
  bring: z
    .object({
      // Perks raise the pocket limits; the server clamps to the survivor's own (bringLimits).
      bandage: z.number().int().min(0).max(30).optional(),
      medkit: z.number().int().min(0).max(15).optional(),
      /** Ammo and Fuel for the weapon (krug 4, E4). Missing: the recommended amount. */
      // 7. 10. 2026 (Ammo per shot): a run of 8 hours fires about 150 rounds (`AMMO_RULES.maxBring`).
      ammo: z.number().int().min(0).max(400).optional(),
      fuel: z.number().int().min(0).max(60).optional(),
      painkillers: z.boolean().optional(),
      stim: z.boolean().optional(),
      /** 7. 10. 2026 (owner): Hollow-point Rounds (one box, a firearm) and Molotovs (pockets: `bringLimits`). */
      hollowPoint: z.boolean().optional(),
      molotov: z.number().int().min(0).max(10).optional(),
    })
    .default({}),
  /** 7. 10. 2026 (owner, round 2): take the truck (Fuel by the zone, more pack room by `TRUCK_RULES`, runs of 20 minutes or more). */
  truck: z.boolean().optional(),
  /** Krug 5 (K12.3): the meal eaten before the run (one, from Storage), or none. Old clients: none. */
  // Package 4, part O: the new meals (a Horde Ration sent with an ordinary run is not eaten: it is for the horde).
  meal: z.enum(['ration', 'dried_jerky', 'hot_stew', 'cold_beans', 'canned_feast', 'station9_chili', 'black_coffee', 'halcyon_bar', 'canned_peaches', 'fever_broth', 'night_stew', 'horde_ration']).nullable().optional(),
  revengeTokenId: z.string().max(64).optional().nullable(),
});

/** 7. 10. 2026 (owner): the generator at Station 9 on or off. */
export const generatorSchema = z.object({ on: z.boolean() }).strict();

/** The wardrobe (docs/OUTFITI_garderoba.md, 4.4): an outfit, or `{ outfit: null }` for the base look. */
// 20. 10. 2026 (owner): `{ match: true }` is Match my gear (the portrait follows the worn armor); the other two are the manual choice.
export const selectOutfitSchema = z.union([z.object({ set: z.enum(OUTFIT_SET_IDS), rarity: z.enum(OUTFIT_RARITIES) }).strict(), z.object({ outfit: z.null() }).strict(), z.object({ match: z.literal(true) }).strict()]);

/** 20. 10. 2026 (owner): a new name for the survivor (the same rules as at creation). */
export const renameSurvivorSchema = z.object({ name: survivorNameSchema }).strict();

/** Krug 5 (K7.1): the ids from the preview; the server checks every one again. */
export const dismantleBulkSchema = z.object({ itemIds: z.array(z.string().min(1).max(64)).min(1).max(BULK_DISMANTLE_MAX) });

export const decideSchema = z.object({ action: z.enum(['trade', 'attack', 'steal', 'escape', 'follow']) });

/** `count` (26. 9. 2026): the same recipe queued several times at once, up to the free queue slots. */
export const craftSchema = z.object({
  recipeId: z.string().min(1).max(60),
  count: z.number().int().min(1).max(CRAFT_QUEUE_LIMIT).default(1),
  /** 4. 10. 2026 (package 3, K): a Supplies batch (×2, ×5) as one job. */
  batch: z.number().int().min(2).max(10).optional(),
});
/** 6. 10. 2026 (Workshop 9): cancel a batch's piece alone, or the whole batch (`batch: true`, every piece left). */
export const cancelCraftSchema = z.object({ batch: z.boolean().optional() }).default({});
/** 4. 10. 2026 (package 3, J2): the three slots of a Relic Forge roll, and the candidate chosen. */
export const relicRollSchema = z.object({ slots: z.array(z.enum(RELIC_SLOTS)).length(RELIC_FORGE.picks) });
export const relicChooseSchema = z.object({ itemId: z.string().min(1).max(60) });
/** Package 3 (I): using a Blueprint (the item picked from its options; none when it only turns into materials). */
export const blueprintUseSchema = z.object({ templateId: z.string().min(1).max(60).nullable().default(null) });
/** Package 3 (H): a Rework and its donors. */
export const reworkSchema = z.object({ donors: z.array(z.string().min(1).max(60)).min(1).max(3) });
/** 11. 10. 2026 (BACKLOG #36): Recalibrate: the index of the affix that changes. */
export const recalibrateSchema = z.object({ affix: z.number().int().min(0).max(8) });

export const adClaimSchema = z.object({ reward: z.enum(['energy', 'heal']), expeditionId: z.string().max(64).optional().nullable() });
/** Krug 5 (K11): the Trader. `key` makes a purchase count once however often it is sent. */
export const traderBuySchema = z.object({ offerId: z.string().min(1).max(40), qty: z.number().int().min(1).max(10).default(1), key: z.string().min(8).max(64).optional() });
export const traderSellSchema = z.object({ resource: zStackKind, qty: z.number().int().min(1).max(100000) });
export const traderRefreshSchema = z.object({ via: z.enum(['free']).default('free') });
/**
 * 23. 10. 2026 (owner, a player's feedback): a put on a building site. Without `stacks` (the button) everything Storage has
 * toward the next level's cost; with them at most that much of each.
 */
export const buildingSiteSchema = z.object({ stacks: z.partialRecord(zStackKind, z.number().int().min(1).max(100000)).optional() }).strict().default({});
/** Krug 5 (K15.2): claim a season goal's reward. */
// 2. 10. 2026: the goal numbers run past 12 since 30. 9. (now up to 35); the server answers an unknown one
// with `No such season goal`. The old max(12) refused every goal numbered 13 and up.
export const seasonClaimSchema = z.object({ goal: z.number().int().min(1).max(999) });
/** Krug 5 (K16.2): the first use of a system just opened (the `New` dot goes). */
export const unlockUsedSchema = z.object({ feature: z.string().min(1).max(40) });

export const emailStartSchema = z.object({ email: z.string().trim().toLowerCase().email().max(200) });

export const clanCreateSchema = z.object({
  name: z.string().trim().min(3).max(24).regex(/^[A-Za-z0-9][A-Za-z0-9 '\-]*$/),
  // 11. 10. 2026 (owner, BACKLOG #33): a crew's tag is 3–4 letters or digits (was 2–5).
  tag: z.string().trim().min(3).max(4).regex(/^[A-Za-z0-9]+$/).transform((s) => s.toUpperCase()),
  description: z.string().trim().max(200).default(''),
  open: z.boolean().default(true),
});

export const emblemSchema = z.object({
  shape: z.enum(['shield', 'circle', 'diamond', 'banner', 'hex', 'pennant']),
  symbol: z.enum(['axes', 'crow', 'gear', 'radio', 'road', 'wolf']),
  field: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  ink: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
});

export const donateSchema = z.object({ stacks: z.partialRecord(zStackKind, z.number().int().min(1).max(10000)) });

export const chatSchema = z.object({ body: z.string().trim().min(1).max(300) });

/**
 * A player picked from a search (userId), or typed in: an invite code or a name. Names
 * are not unique (8.1), so a name only works when exactly one survivor has it.
 */
export const friendAddSchema = z
  .object({ userId: z.string().min(1).max(40).optional(), name: z.string().trim().min(2).max(40).optional() })
  .refine((b) => !!b.userId || !!b.name, 'Pick a survivor');

export const playerSearchSchema = z.object({ q: z.string().trim().min(2).max(40) });

export const notifPrefsSchema = z.object({
  push: z.boolean(),
  email: z.boolean(),
  telegram: z.boolean(),
  encounters: z.boolean(),
  energyFull: z.boolean().optional(),
});

export const pushSubscribeSchema = z.object({
  endpoint: z.string().url().max(1000),
  keys: z.object({ p256dh: z.string().max(200), auth: z.string().max(100) }),
});

export const newsletterSchema = z.object({ email: z.string().trim().toLowerCase().email().max(200) });

/** Feedback button (krug 3, U5): a short text, the screen, an optional scaled-down screenshot. */
export const FEEDBACK_RULES = { minText: 3, maxText: 2000, maxScreenshotChars: 1_500_000, screenshotMaxSide: 1280 } as const;
export const feedbackSchema = z.object({
  text: z.string().trim().min(FEEDBACK_RULES.minText, 'Write a few words').max(FEEDBACK_RULES.maxText),
  page: z.string().max(200).optional().nullable(),
  screenshot: z
    .string()
    .max(FEEDBACK_RULES.maxScreenshotChars, 'That screenshot is too big')
    .regex(/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/, 'Screenshots must be PNG, JPEG or WebP')
    .optional()
    .nullable(),
});

export const shareCreateSchema = z.object({
  expeditionId: z.string().max(64).optional(),
  hordeId: z.string().max(64).optional(),
  variant: z.enum(['ambush', 'legendary', 'escape', 'discovery', 'horde']).optional(),
});

export type CreateSurvivorBody = z.infer<typeof createSurvivorSchema>;
export type SendExpeditionInput = z.infer<typeof sendExpeditionSchema>;

// 9. 10. 2026 (owner): a highest tier per rarity (0 = off). The old shape (rarities + one maxTier, a client from
// before) is still taken and turned into the new one, so the handler always gets `{ enabled, tiers }`.
export const autoDismantleSchema = z
  .union([
    z.object({
      enabled: z.boolean(),
      tiers: z.partialRecord(z.enum(AUTO_DISMANTLE_RARITIES), z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3), z.literal(4)])),
    }),
    z.object({
      enabled: z.boolean(),
      rarities: z.array(z.enum(AUTO_DISMANTLE_RARITIES)).max(4),
      maxTier: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
    }),
  ])
  .transform((r) => normalizeAutoDismantle(r));

export const itemLockSchema = z.object({ locked: z.boolean() });

export const equipBestSchema = z.object({
  changes: z.array(z.object({ slot: z.enum(GEAR_SLOTS), to: z.string().min(1).max(64).nullable() })).max(GEAR_SLOTS.length),
});
